import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Box, Container, Title, Stack, Group, SimpleGrid } from '@mantine/core'
import { AppButton, AppCard, AppText } from '../../app/common/ui'
import { IoConstructOutline, IoCogOutline, IoListOutline, IoShieldCheckmarkOutline, IoPeopleOutline, IoTrophyOutline } from 'react-icons/io5'
import agent, { type TrendingResult, type StatsDto } from '../../app/api/agent'
import AmmoScoreBadge from '../../app/common/ui/AmmoScoreBadge'
import MotdBanner from '../../app/common/ui/MotdBanner'
import GunSlopWordmark from '../../app/common/ui/GunSlopWordmark'
import { postTypeLabel } from '@gunslop/ui'
import heroLogoUrl from '../../app/icon-ak-sil-no-bak/icon-styled.svg?url'

const CROSSHAIR = {
  backgroundImage: 'repeating-linear-gradient(0deg, rgba(232,93,4,0.04) 0px, transparent 1px, transparent 20px), repeating-linear-gradient(90deg, rgba(232,93,4,0.04) 0px, transparent 1px, transparent 20px)'
}

const HERO_LOOP = '5s ease-in-out infinite'

const heroMotionReduce = {
  transition: 'none',
  transform: 'none',
  opacity: 1,
  animation: 'none'
} as const

export default function Homepage () {
  const [stats, setStats] = useState<StatsDto | null>(null)
  const [trending, setTrending] = useState<TrendingResult | null>(null)
  const [heroBrandActive, setHeroBrandActive] = useState(false)

  useEffect(() => {
    agent.Stats.get()
      .then(setStats)
      .catch(() => setStats(null))
  }, [])

  useEffect(() => {
    agent.Feed.trending(3, 7)
      .then(setTrending)
      .catch(() => setTrending(null))
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => setHeroBrandActive(true), 100)
    return () => window.clearTimeout(timer)
  }, [])

  const topPosts = trending?.posts?.slice(0, 3) ?? []

  return (
    <>
      <MotdBanner layout="landing" />
      {/* Section 1 — Hero */}
      <Box
        sx={{
          width: '100%',
          ...CROSSHAIR,
          padding: '80px 24px 60px',
          position: 'relative'
        }}
        style={{ background: 'var(--gs-bg)' }}
      >
        {/* Top-right auth shortcuts */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 24,
            display: 'flex',
            gap: 8
          }}
        >
          <AppButton
            component={Link}
            to="/login"
            size="xs"
            sx={{
              background: '#21262D',
              border: '1.5px solid #484F58',
              color: '#E6EDF3',
              fontFamily: 'DM Sans, sans-serif',
              padding: '4px 10px',
              height: 28
            }}
          >
            Sign in
          </AppButton>
          <AppButton
            component={Link}
            to="/register"
            size="xs"
            variant="outline"
            sx={{
              background: 'transparent',
              border: '1.5px solid #484F58',
              color: '#E6EDF3',
              fontFamily: 'DM Sans, sans-serif',
              padding: '4px 10px',
              height: 28
            }}
          >
            Register
          </AppButton>
        </Box>
        <Container size="md">
          <Stack align="center" spacing="md">
            <Stack
              align="center"
              spacing="md"
              sx={{
                transition: 'transform 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 1.5s ease-out',
                transform: heroBrandActive ? 'scale(1)' : 'scale(0.95)',
                opacity: heroBrandActive ? 1 : 0,
                '@media (prefers-reduced-motion: reduce)': heroMotionReduce
              }}
            >
              <Box
                component="img"
                src={heroLogoUrl}
                alt=""
                sx={{
                  width: 128,
                  height: 128,
                  borderRadius: 12,
                  transformOrigin: 'center center',
                  '@keyframes heroLogoPulse': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.05)' }
                  },
                  animation: heroBrandActive ? `heroLogoPulse ${HERO_LOOP}` : 'none',
                  '@media (prefers-reduced-motion: reduce)': heroMotionReduce
                }}
              />
              <Box sx={{ margin: 0, perspective: 640 }}>
                <Box
                  component="h1"
                  sx={{
                    margin: 0,
                    transformOrigin: 'center center',
                    transformStyle: 'preserve-3d',
                    '@keyframes heroWordmarkRock': {
                      '0%, 100%': { transform: 'rotateX(-10deg)' },
                      '50%': { transform: 'rotateX(10deg)' }
                    },
                    animation: heroBrandActive ? `heroWordmarkRock ${HERO_LOOP}` : 'none',
                    '@media (prefers-reduced-motion: reduce)': heroMotionReduce
                  }}
                >
                  <GunSlopWordmark size="hero" />
                </Box>
              </Box>
            </Stack>
            <AppText
              size="lg"
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 18,
                color: 'var(--gs-muted)',
                maxWidth: 500,
                textAlign: 'center'
              }}
            >
              Reviews, builds, and parts intel from people who actually know. Community-curated. Zero fluff.
            </AppText>
            <Group spacing="sm">
              <AppButton
                component={Link}
                to="/login"
                color="ember"
                size="md"
                sx={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Sign in →
              </AppButton>
              <AppButton
                component={Link}
                to="/registry"
                variant="outline"
                size="md"
                sx={{
                  background: 'var(--gs-surface)',
                  border: '1.5px solid var(--gs-border)',
                  color: 'var(--gs-text)',
                  fontFamily: 'DM Sans, sans-serif'
                }}
              >
                Browse the Registry
              </AppButton>
            </Group>
            <AppText
              size="xs"
              sx={{
                fontFamily: 'DM Sans, sans-serif',
                color: 'var(--gs-muted)',
                marginTop: 8
              }}
            >
              New here?{' '}
              <AppText
                component={Link}
                to="/register"
                sx={{ color: '#58A6FF', display: 'inline' }}
              >
                Create an account →
              </AppText>
            </AppText>
            {/* Stats pills */}
            <Group spacing="sm" mt="lg">
              <Box
                sx={{
                  background: 'var(--gs-surface)',
                  border: '1px solid var(--gs-border)',
                  borderRadius: 20,
                  padding: '6px 16px',
                  fontSize: 12,
                  color: 'var(--gs-muted)',
                  fontFamily: 'DM Sans, sans-serif'
                }}
              >
                <AppText component="span" sx={{ color: '#E85D04', fontWeight: 700 }}>
                  {stats != null ? stats.totalFirearmModels : '—'}
                </AppText>{' '}
                Firearms
              </Box>
              <Box
                sx={{
                  background: 'var(--gs-surface)',
                  border: '1px solid var(--gs-border)',
                  borderRadius: 20,
                  padding: '6px 16px',
                  fontSize: 12,
                  color: 'var(--gs-muted)',
                  fontFamily: 'DM Sans, sans-serif'
                }}
              >
                <AppText component="span" sx={{ color: '#E85D04', fontWeight: 700 }}>
                  {stats != null ? stats.totalParts : '—'}
                </AppText>{' '}
                Parts
              </Box>
              <Box
                sx={{
                  background: 'var(--gs-surface)',
                  border: '1px solid var(--gs-border)',
                  borderRadius: 20,
                  padding: '6px 16px',
                  fontSize: 12,
                  color: 'var(--gs-muted)',
                  fontFamily: 'DM Sans, sans-serif'
                }}
              >
                <AppText component="span" sx={{ color: '#E85D04', fontWeight: 700 }}>
                  {stats != null ? stats.totalBuilds : '—'}
                </AppText>{' '}
                Builds
              </Box>
            </Group>
          </Stack>
        </Container>
      </Box>

      {/* Section 2 — What's Hot */}
      <Box sx={{ background: 'var(--gs-bg)', padding: '48px 24px' }}>
        <Container size="lg">
          <AppText
            component="h2"
            sx={{
              fontFamily: 'Oswald, sans-serif',
              fontWeight: 500,
              fontSize: 14,
              color: 'var(--gs-muted)',
              letterSpacing: 2,
              marginBottom: 24,
              '&::after': {
                content: '""',
                display: 'block',
                width: 60,
                height: 2,
                background: '#E85D04',
                marginTop: 8
              }
            }}
          >
            WHAT'S HOT RIGHT NOW
          </AppText>
          <SimpleGrid cols={3} breakpoints={[{ maxWidth: 768, cols: 1 }]} spacing="lg">
            {topPosts.length === 0
              ? (
              <AppText size="sm" color="dimmed">
                No trending posts yet.
              </AppText>
                )
              : (
                  topPosts.map((p) => (
                <AppCard
                  key={p.id}
                  component={Link}
                  to={`/blog/${p.id}`}
                  padding="sm"
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    background: 'var(--gs-surface)',
                    border: '1px solid var(--gs-border)',
                    borderRadius: 8,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      height: 120,
                      background: (p.mainPhotoUrl ?? p.coverImageUrl)
                        ? `url(${p.mainPhotoUrl ?? p.coverImageUrl}) center/cover`
                        : 'linear-gradient(135deg, var(--gs-surface), var(--gs-bg))',
                      borderRadius: 6,
                      marginBottom: 8
                    }}
                  />
                  <AppText size="xs" sx={{ color: 'var(--gs-muted)', marginBottom: 4 }}>
                    {postTypeLabel(p.type ?? 0)}
                  </AppText>
                  <AppText weight={600} size="sm" sx={{ fontFamily: 'Oswald, sans-serif', marginBottom: 6 }}>
                    {p.title}
                  </AppText>
                  <Group spacing="xs">
                    <AppText size="xs" color="dimmed">
                      {p.authorDisplayName || p.authorUsername}
                    </AppText>
                    <AmmoScoreBadge tier={p.authorAmmoTier ?? 'Newcomer'} score={p.authorAmmoScore ?? 0} size="sm" />
                    <AppText size="xs" sx={{ color: 'var(--gs-green)' }}>
                      {p.voteScore ?? 0} votes
                    </AppText>
                  </Group>
                </AppCard>
                  ))
                )}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Section 3 — Feature highlights */}
      <Box sx={{ background: 'var(--gs-bg)', padding: '48px 24px' }}>
        <Container size="lg">
          <SimpleGrid cols={3} breakpoints={[{ maxWidth: 960, cols: 2 }, { maxWidth: 640, cols: 1 }]} spacing="lg">
            {[
              {
                icon: <IoConstructOutline size={22} color="var(--gs-accent)" />,
                label: 'Community Builds',
                desc: 'Document your builds part-by-part. Community votes surface the best configurations.',
                href: '/builds'
              },
              {
                icon: <IoCogOutline size={22} color="#58A6FF" />,
                label: 'Curated Parts Bin',
                desc: 'Every part submitted goes through community vetting. No spam, no fakes.',
                href: '/parts-bin'
              },
              {
                icon: <IoListOutline size={22} color="#3FB950" />,
                label: 'Firearm Registry',
                desc: 'The most detailed community-curated registry of makes and models in the US.',
                href: '/registry'
              },
              {
                icon: <IoShieldCheckmarkOutline size={22} color="#C9A84C" />,
                label: 'Verified Reviews',
                desc: 'Reviews from owners only. AmmoScore ranks contributors by quality, not quantity.',
                href: '/blog'
              },
              {
                icon: <IoPeopleOutline size={22} color="var(--gs-muted)" />,
                label: 'Gun Vault',
                desc: 'Track every firearm you own. Share your collection or keep it private.',
                href: '/gun-vault'
              },
              {
                icon: <IoTrophyOutline size={22} color="#E85D04" />,
                label: 'AmmoScore',
                desc: 'Earn reputation for every quality contribution. Rise from Newcomer to Armorer.',
                href: '/discover'
              }
            ].map((f) => (
              <AppCard
                key={f.label}
                component={Link}
                to={f.href}
                padding="md"
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  background: 'var(--gs-surface)',
                  border: '1px solid var(--gs-border)',
                  borderRadius: 8,
                  transition: 'border-color 150ms',
                  '&:hover': { borderColor: 'var(--gs-border-bright)' }
                }}
              >
                <Box sx={{ marginBottom: 10 }}>{f.icon}</Box>
                <AppText weight={600} sx={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', fontSize: 13, letterSpacing: 0.5, marginBottom: 4 }}>
                  {f.label}
                </AppText>
                <AppText size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)', lineHeight: 1.6 }}>
                  {f.desc}
                </AppText>
              </AppCard>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Section 4 — CTA */}
      <Box sx={{ background: 'var(--gs-surface)', borderTop: '1px solid var(--gs-border)', padding: '64px 24px' }}>
        <Container size="sm">
          <Box sx={{ textAlign: 'center' }}>
            <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: 'var(--gs-muted)', marginBottom: 12 }}>
              Free to join
            </AppText>
            <Title
              order={2}
              sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 32, color: 'var(--gs-text)', marginBottom: 8, lineHeight: 1.1 }}
            >
              THE REFERENCE LAYER FOR THE<br />
              <AppText component="span" sx={{ color: 'var(--gs-accent)' }}>US FIREARMS COMMUNITY</AppText>
            </Title>
            <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: 'var(--gs-muted)', maxWidth: 420, margin: '0 auto 28px' }}>
              Post reviews. Document builds. Build reputation. One account, every firearm resource you need.
            </AppText>
            <Group position="center" spacing="sm">
              <AppButton component={Link} to="/register" size="md" sx={{ background: 'var(--gs-accent)', color: '#fff', fontFamily: 'DM Sans, sans-serif', '&:hover': { background: '#bf4a00' } }}>
                Create free account
              </AppButton>
              <AppButton component={Link} to="/registry" variant="outline" size="md" sx={{ borderColor: 'var(--gs-border)', color: 'var(--gs-text)', fontFamily: 'DM Sans, sans-serif' }}>
                Browse the Registry
              </AppButton>
            </Group>
            <Box
              component={Link}
              to="/about"
              sx={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 6,
                marginTop: 28,
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 13,
                color: 'var(--gs-muted)',
                textDecoration: 'none',
                '&:hover': { color: 'var(--gs-accent)' }
              }}
            >
              <span>About</span>
              <GunSlopWordmark
                size="xs"
                inline
                sx={{ fontSize: 13, letterSpacing: 1, color: 'var(--gs-muted)' }}
              />
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  )
}
